import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useParams, useLocation } from 'react-router-dom';
import { javaQuestions, getAllJavaQuestions } from '../data/javaQuestions';
import { dbmsQuestions, getAllDbmsQuestions, shuffleArray } from '../data/dbmsQuestions';
import { pythonQuestions, getAllPythonQuestions } from '../data/pythonQuestions';
import { osQuestions, getAllOsQuestions } from '../data/osQuestions';
import { cnQuestions, getAllCnQuestions } from '../data/cnQuestions';
import { mlQuestions, getAllMlQuestions } from '../data/mlQuestions';
import { aiGenaiQuestions, getAllAiGenaiQuestions } from '../data/aiGenaiQuestions';

const VALID_SUBJECTS = ['java', 'dbms', 'python', 'os', 'cn', 'ml', 'ai-genai'];

const SUBJECT_LABELS = {
  java: 'Java',
  dbms: 'DBMS',
  python: 'Python',
  os: 'Operating Systems',
  cn: 'Computer Networks',
  ml: 'Machine Learning',
  'ai-genai': 'AI / GenAI'
};

const TOPIC_TITLES = {
  java: {
    'core-java': 'Core Java Concepts',
    oops: 'Object-Oriented Programming',
    memory: 'Memory & Execution',
    'exception-handling': 'Exception Handling',
    multithreading: 'Multithreading & Concurrency',
    collections: 'Collections Framework'
  },
  dbms: {
    basics: 'DBMS Basics',
    'sql-basics': 'SQL Fundamentals',
    normalization: 'Normalization & Design',
    transactions: 'Transactions & Concurrency',
    indexing: 'Indexing & Optimization',
    advanced: 'Advanced Concepts'
  },
  python: {
    basics: 'Python Basics',
    'data-types': 'Data Types',
    functions: 'Functions',
    oop: 'Object-Oriented Python',
    'memory-and-scope': 'Memory & Scope',
    'comprehensions-and-iterators': 'Comprehensions & Iterators',
    'exception-handling': 'Exception Handling',
    advanced: 'Advanced Python'
  },
  os: {
    basics: 'OS Basics',
    'process-management': 'Process Management',
    'cpu-scheduling': 'CPU Scheduling',
    'memory-management': 'Memory Management',
    synchronization: 'Synchronization',
    'file-systems': 'File Systems'
  },
  cn: {
    basics: 'Network Basics',
    'osi-tcpip': 'OSI & TCP/IP',
    'ip-addressing': 'IP Addressing',
    protocols: 'Protocols',
    'tcp-udp': 'TCP vs UDP',
    'network-devices': 'Network Devices',
    'routing-switching': 'Routing & Switching',
    security: 'Network Security'
  },
  ml: {
    'ml-fundamentals': 'ML Fundamentals',
    'neural-networks': 'Neural Networks',
    cnn: 'Convolutional Networks',
    'rnn-transformers': 'RNN & Transformers',
    'training-optimization': 'Training & Optimization',
    'evaluation-metrics': 'Evaluation Metrics',
    advanced: 'Advanced ML'
  },
  'ai-genai': {
    'llm-fundamentals': 'LLM Fundamentals',
    prompting: 'Prompting',
    rag: 'RAG',
    embeddings: 'Embeddings',
    agents: 'Agents',
    'fine-tuning-and-alignment': 'Fine-Tuning & Alignment',
    evaluation: 'Evaluation'
  }
};

const TOPIC_ICONS = {
  java: {
    'core-java': '☕',
    oops: '🏗️',
    memory: '🧠',
    'exception-handling': '⚠️',
    multithreading: '🔄',
    collections: '📚'
  },
  dbms: {
    basics: '💾',
    'sql-basics': '🔍',
    normalization: '📐',
    transactions: '🔄',
    indexing: '⚡',
    advanced: '🚀'
  },
  python: {
    basics: '🐍',
    'data-types': '📦',
    functions: '⚙️',
    oop: '🏗️',
    'memory-and-scope': '🧠',
    'comprehensions-and-iterators': '🔁',
    'exception-handling': '⚠️',
    advanced: '🚀'
  },
  os: {
    basics: '💻',
    'process-management': '📋',
    'cpu-scheduling': '⏱️',
    'memory-management': '🧠',
    synchronization: '🔒',
    'file-systems': '📁'
  },
  cn: {
    basics: '🌐',
    'osi-tcpip': '📶',
    'ip-addressing': '🔗',
    protocols: '📡',
    'tcp-udp': '🔀',
    'network-devices': '🛜',
    'routing-switching': '🗺️',
    security: '🔐'
  },
  ml: {
    'ml-fundamentals': '📊',
    'neural-networks': '🧠',
    cnn: '👁️',
    'rnn-transformers': '🔤',
    'training-optimization': '⚡',
    'evaluation-metrics': '📈',
    advanced: '🚀'
  },
  'ai-genai': {
    'llm-fundamentals': '🤖',
    prompting: '💬',
    rag: '📚',
    embeddings: '🔗',
    agents: '🎯',
    'fine-tuning-and-alignment': '⚖️',
    evaluation: '✅'
  }
};

const TOPIC_COLORS = {
  java: {
    'core-java': 'from-blue-500 to-indigo-600',
    oops: 'from-green-500 to-teal-600',
    memory: 'from-purple-500 to-pink-600',
    'exception-handling': 'from-red-500 to-orange-600',
    multithreading: 'from-cyan-500 to-blue-600',
    collections: 'from-yellow-500 to-orange-600'
  },
  dbms: {
    basics: 'from-blue-500 to-indigo-600',
    'sql-basics': 'from-green-500 to-teal-600',
    normalization: 'from-purple-500 to-pink-600',
    transactions: 'from-red-500 to-orange-600',
    indexing: 'from-yellow-500 to-orange-600',
    advanced: 'from-cyan-500 to-blue-600'
  },
  python: {
    basics: 'from-blue-500 to-indigo-600',
    'data-types': 'from-green-500 to-teal-600',
    functions: 'from-purple-500 to-pink-600',
    oop: 'from-red-500 to-orange-600',
    'memory-and-scope': 'from-cyan-500 to-blue-600',
    'comprehensions-and-iterators': 'from-yellow-500 to-orange-600',
    'exception-handling': 'from-rose-500 to-red-600',
    advanced: 'from-violet-500 to-purple-600'
  },
  os: {
    basics: 'from-blue-500 to-indigo-600',
    'process-management': 'from-green-500 to-teal-600',
    'cpu-scheduling': 'from-purple-500 to-pink-600',
    'memory-management': 'from-red-500 to-orange-600',
    synchronization: 'from-cyan-500 to-blue-600',
    'file-systems': 'from-yellow-500 to-orange-600'
  },
  cn: {
    basics: 'from-blue-500 to-indigo-600',
    'osi-tcpip': 'from-green-500 to-teal-600',
    'ip-addressing': 'from-purple-500 to-pink-600',
    protocols: 'from-red-500 to-orange-600',
    'tcp-udp': 'from-cyan-500 to-blue-600',
    'network-devices': 'from-yellow-500 to-orange-600',
    'routing-switching': 'from-emerald-500 to-teal-600',
    security: 'from-slate-600 to-gray-800'
  },
  ml: {
    'ml-fundamentals': 'from-blue-500 to-indigo-600',
    'neural-networks': 'from-green-500 to-teal-600',
    cnn: 'from-purple-500 to-pink-600',
    'rnn-transformers': 'from-red-500 to-orange-600',
    'training-optimization': 'from-cyan-500 to-blue-600',
    'evaluation-metrics': 'from-yellow-500 to-orange-600',
    advanced: 'from-violet-500 to-purple-600'
  },
  'ai-genai': {
    'llm-fundamentals': 'from-blue-500 to-indigo-600',
    prompting: 'from-green-500 to-teal-600',
    rag: 'from-purple-500 to-pink-600',
    embeddings: 'from-red-500 to-orange-600',
    agents: 'from-cyan-500 to-blue-600',
    'fine-tuning-and-alignment': 'from-yellow-500 to-orange-600',
    evaluation: 'from-violet-500 to-purple-600'
  }
};

function loadQuestionsForSubject(subject, topicParam) {
  if (topicParam === 'random') {
    switch (subject) {
      case 'java':
        return shuffleArray(getAllJavaQuestions());
      case 'dbms':
        return shuffleArray(getAllDbmsQuestions());
      case 'python':
        return shuffleArray(getAllPythonQuestions());
      case 'os':
        return shuffleArray(getAllOsQuestions());
      case 'cn':
        return shuffleArray(getAllCnQuestions());
      case 'ml':
        return shuffleArray(getAllMlQuestions());
      case 'ai-genai':
        return shuffleArray(getAllAiGenaiQuestions());
      default:
        return [];
    }
  }
  switch (subject) {
    case 'java':
      return javaQuestions[topicParam] || [];
    case 'dbms':
      return dbmsQuestions[topicParam] || [];
    case 'python':
      return pythonQuestions[topicParam] || [];
    case 'os':
      return osQuestions[topicParam] || [];
    case 'cn':
      return cnQuestions[topicParam] || [];
    case 'ml':
      return mlQuestions[topicParam] || [];
    case 'ai-genai':
      return aiGenaiQuestions[topicParam] || [];
    default:
      return [];
  }
}

const TopicPage = () => {
  const { topic } = useParams();
  const location = useLocation();
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [flippedCards, setFlippedCards] = useState(new Set());
  const [showingAnswer, setShowingAnswer] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  const subject = useMemo(() => {
    const seg = location.pathname.split('/').filter(Boolean)[0];
    if (VALID_SUBJECTS.includes(seg)) return seg;
    return null;
  }, [location.pathname]);

  useEffect(() => {
    setIsLoading(true);
    if (!subject || !topic) {
      setQuestions([]);
    } else {
      setQuestions(loadQuestionsForSubject(subject, topic));
    }
    setCurrentIndex(0);
    setFlippedCards(new Set());
    setShowingAnswer(false);
    setIsLoading(false);
  }, [topic, subject]);

  const subjectLabel = subject ? SUBJECT_LABELS[subject] : 'Topics';
  const basePath = subject ? `/${subject}` : '/';

  const displayTitle =
    topic === 'random' && subject
      ? `Random ${subjectLabel} Questions`
      : subject && TOPIC_TITLES[subject]?.[topic];

  const displayIcon =
    topic === 'random' ? '🎲' : subject ? TOPIC_ICONS[subject]?.[topic] ?? '📖' : '📖';

  const displayColor =
    topic === 'random'
      ? 'from-indigo-500 to-purple-600'
      : subject
        ? TOPIC_COLORS[subject]?.[topic] ?? 'from-gray-500 to-gray-600'
        : 'from-gray-500 to-gray-600';

  const handleCardFlip = useCallback(() => {
    setShowingAnswer(!showingAnswer);
    setFlippedCards(prev => new Set([...prev, currentIndex]));
  }, [showingAnswer, currentIndex]);

  const handleNext = useCallback(() => {
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setShowingAnswer(false);
    }
  }, [currentIndex, questions.length]);

  const handlePrevious = useCallback(() => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      setShowingAnswer(false);
    }
  }, [currentIndex]);

  const handleKeyPress = useCallback((e) => {
    switch(e.key) {
      case ' ':
        e.preventDefault();
        handleCardFlip();
        break;
      case 'ArrowRight':
        handleNext();
        break;
      case 'ArrowLeft':
        handlePrevious();
        break;
      default:
        break;
    }
  }, [handleCardFlip, handleNext, handlePrevious]);

  const onTouchStart = useCallback((e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  }, []);

  const onTouchMove = useCallback((e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  }, []);

  const onTouchEnd = useCallback(() => {
    if (!touchStart || !touchEnd) return;
    
    const minSwipeDistance = 50;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe && currentIndex < questions.length - 1) {
      handleNext();
    } else if (isRightSwipe && currentIndex > 0) {
      handlePrevious();
    }
  }, [touchStart, touchEnd, currentIndex, questions.length, handleNext, handlePrevious]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [handleKeyPress]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading questions...</p>
        </div>
      </div>
    );
  }

  if (questions.length === 0 || !displayTitle) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">😅</div>
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Topic not found</h1>
          <p className="text-gray-600 mb-6">This topic doesn't exist or has no questions yet.</p>
          <Link 
            to={basePath}
            className="inline-flex items-center px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
          >
            ← Back to {subjectLabel} Topics
          </Link>
        </div>
      </div>
    );
  }

  const currentQuestion = questions[currentIndex];
  const progress = ((currentIndex + 1) / questions.length) * 100;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <nav className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <Link 
              to={basePath}
              className="flex items-center space-x-2 hover:opacity-80 transition-opacity"
            >
              <span className="text-xl">←</span>
              <span className="font-medium text-gray-700">
                Back to {subjectLabel}
              </span>
            </Link>
            
            <div className="flex items-center space-x-4">
              <div className="text-sm text-gray-600">
                {currentIndex + 1} of {questions.length}
              </div>
              <div className="w-32 bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center mb-8">
          <div className="text-4xl mb-2">{displayIcon}</div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
            {displayTitle}
          </h1>
          <p className="text-gray-600">
            {topic === 'random' 
              ? `Mixed questions from all ${subjectLabel} topics` 
              : 'Master the concepts with interactive flashcards'
            }
          </p>
        </div>

        <div className="relative mb-8">
          <div 
            className={`card-flip ${showingAnswer ? 'flipped' : ''} cursor-pointer touch-pan-y`}
            onClick={handleCardFlip}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          >
            <div className="card-inner h-96">
              <div className={`card-front bg-gradient-to-br ${displayColor} text-white flex items-center justify-center shadow-2xl`}>
                <div className="text-center p-8">
                  <div className="text-sm font-medium mb-4 bg-white/20 backdrop-blur-sm rounded-full px-4 py-2 inline-block">
                    Question {currentIndex + 1}
                  </div>
                  <h2 className="text-xl md:text-2xl font-semibold leading-relaxed">
                    {currentQuestion.question}
                  </h2>
                  <div className="mt-8 text-sm opacity-80">
                    <div>Click or press Space to reveal answer</div>
                    <div className="mt-2 md:hidden text-xs">Swipe left/right to navigate</div>
                  </div>
                </div>
              </div>

              <div className="card-back bg-white text-gray-800 flex items-center justify-center shadow-2xl border">
                <div className="text-center p-8">
                  <div className="text-sm font-medium text-green-600 mb-4 bg-green-50 rounded-full px-4 py-2 inline-block">
                    Answer
                  </div>
                  <div className="text-base md:text-lg leading-relaxed text-left max-w-2xl">
                    {currentQuestion.answer}
                  </div>
                  <div className="mt-8 text-sm text-gray-500">
                    <div>Click or press Space to see question again</div>
                    <div className="mt-2 md:hidden text-xs">Swipe left/right to navigate</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-between items-center mb-8">
          <button
            onClick={handlePrevious}
            disabled={currentIndex === 0}
            className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-medium transition-all ${
              currentIndex === 0 
                ? 'bg-gray-200 text-gray-400 cursor-not-allowed' 
                : 'bg-white text-gray-700 hover:bg-gray-50 shadow-md hover:shadow-lg'
            }`}
          >
            <span>←</span>
            <span>Previous</span>
          </button>

          <div className="flex space-x-4">
            <button
              onClick={handleCardFlip}
              className="px-6 py-3 bg-blue-500 text-white rounded-lg font-medium hover:bg-blue-600 transition-colors shadow-md hover:shadow-lg"
            >
              {showingAnswer ? 'Show Question' : 'Show Answer'}
            </button>
          </div>

          <button
            onClick={handleNext}
            disabled={currentIndex === questions.length - 1}
            className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-medium transition-all ${
              currentIndex === questions.length - 1 
                ? 'bg-gray-200 text-gray-400 cursor-not-allowed' 
                : 'bg-white text-gray-700 hover:bg-gray-50 shadow-md hover:shadow-lg'
            }`}
          >
            <span>Next</span>
            <span>→</span>
          </button>
        </div>

        <div className="grid grid-cols-5 sm:grid-cols-10 gap-2 mb-8">
          {questions.slice(0, 50).map((_, index) => (
            <button
              key={index}
              onClick={() => {setCurrentIndex(index); setShowingAnswer(false);}}
              className={`h-3 rounded-full transition-all ${
                index === currentIndex 
                  ? 'bg-blue-500 scale-125' 
                  : flippedCards.has(index)
                    ? 'bg-green-400'
                    : 'bg-gray-300'
              }`}
              title={`Question ${index + 1}`}
            />
          ))}
          {questions.length > 50 && (
            <div className="h-3 flex items-center text-xs text-gray-500">
              +{questions.length - 50}
            </div>
          )}
        </div>

        <div className="bg-white rounded-lg p-6 shadow-md">
          <h3 className="font-semibold text-gray-800 mb-3">Navigation Controls</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Desktop</h4>
              <div className="space-y-2 text-sm">
                <div className="flex items-center space-x-2">
                  <kbd className="px-2 py-1 bg-gray-100 rounded border text-xs">Space</kbd>
                  <span className="text-gray-600">Flip card</span>
                </div>
                <div className="flex items-center space-x-2">
                  <kbd className="px-2 py-1 bg-gray-100 rounded border text-xs">←</kbd>
                  <span className="text-gray-600">Previous</span>
                </div>
                <div className="flex items-center space-x-2">
                  <kbd className="px-2 py-1 bg-gray-100 rounded border text-xs">→</kbd>
                  <span className="text-gray-600">Next</span>
                </div>
              </div>
            </div>

            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Mobile</h4>
              <div className="space-y-2 text-sm">
                <div className="flex items-center space-x-2">
                  <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">👆 Tap</span>
                  <span className="text-gray-600">Flip card</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs">👈 Swipe</span>
                  <span className="text-gray-600">Next question</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded text-xs">👉 Swipe</span>
                  <span className="text-gray-600">Previous question</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {currentIndex === questions.length - 1 && flippedCards.has(currentIndex) && (
          <div className="mt-8 bg-green-50 border border-green-200 rounded-lg p-6 text-center animate-bounce-in">
            <div className="text-4xl mb-2">🎉</div>
            <h3 className="text-xl font-bold text-green-800 mb-2">Congratulations!</h3>
            <p className="text-green-700 mb-4">
              You've completed all {questions.length} questions in this topic!
            </p>
            <div className="space-x-4">
              <button
                onClick={() => {setCurrentIndex(0); setFlippedCards(new Set()); setShowingAnswer(false);}}
                className="px-6 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
              >
                Review Again
              </button>
              <Link
                to={basePath}
                className="inline-block px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
              >
                Choose Another Topic
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TopicPage;
